import styled from 'styled-components'
import React, { useState, useEffect } from 'react'

import StepCircle from './StepCircle'
import { useOrderSteps } from 'hooks/useOrderSteps'
import { specialStatusKeys } from 'constants/orderStatusConfig'

const ProgressLine = styled.div`
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: linear-gradient(90deg, #22c55e, #16a34a);
    border-radius: inherit;
    transition: width 1s cubic-bezier(0.4, 0, 0.2, 1);
    width: ${props => props.progress}%;
    box-shadow: 0 0 20px rgba(34, 197, 94, 0.3);
  }
`

const DesktopView = ({ steps, currentStep, progressPercentage }) => (
  <div className="hidden md:block space-y-8 px-2 lg:px-6">
    <div className="relative pb-6">
      <div className="absolute top-9 left-16 right-16 h-1 bg-slate-200 rounded-full"></div>
      <ProgressLine
        className="absolute top-9 left-16 right-16 h-1 rounded-full"
        progress={progressPercentage}
      />
      <div className="flex justify-between relative z-10">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep
          const isActive = index === currentStep
          const isSpecial = specialStatusKeys.includes(step.key)
          return (
            <div
              key={index}
              className="flex flex-col items-center max-w-28 lg:max-w-32"
            >
              <div className="p-3">
                <StepCircle
                  isCompleted={isCompleted}
                  isActive={isActive}
                  isSpecial={isSpecial}
                  icon={step.icon}
                />
              </div>
              <div className="text-center">
                <div
                  className={`text-[13px] lg:text-[15px] font-semibold leading-tight transition-colors duration-300 ${
                    isCompleted || isActive
                      ? 'text-slate-900'
                      : 'text-slate-500'
                  }`}
                >
                  {step.label}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  </div>
)

const MobileView = ({ steps, currentStep }) => (
  <div className="md:hidden px-3 sm:px-4 space-y-2 w-full overflow-visible">
    {steps.map((step, index) => {
      const isCompleted = index < currentStep
      const isActive = index === currentStep
      const isSpecial = specialStatusKeys.includes(step.key)
      return (
        <div
          key={index}
          className="flex items-center gap-3 sm:gap-4 py-2 w-full"
        >
          <div className="p-2 flex-shrink-0">
            <StepCircle
              isCompleted={isCompleted}
              isActive={isActive}
              isSpecial={isSpecial}
              icon={step.icon}
            />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <div
              className={`text-sm sm:text-base font-semibold transition-colors duration-300 break-words ${
                isCompleted || isActive ? 'text-slate-900' : 'text-slate-500'
              }`}
              style={{ wordBreak: 'break-word' }}
            >
              <span className="block sm:hidden">{step.shortLabel}</span>
              <span className="hidden sm:block">{step.label}</span>
            </div>
          </div>
        </div>
      )
    })}
  </div>
)

const OrdersProgressBar = ({ statuses, currentStatusKey }) => {
  const { steps } = useOrderSteps(statuses, currentStatusKey)
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    const activeIndex = steps.findIndex(step => step.key === currentStatusKey)
    setCurrentStep(activeIndex >= 0 ? activeIndex : 0)
  }, [currentStatusKey, steps])

  const progressPercentage =
    steps.length <= 1 ? 100 : (currentStep / (steps.length - 1)) * 100

  if (!steps || steps.length === 0) {
    return null
  }

  return (
    <div className="w-full max-w-6xl mx-auto overflow-visible">
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css"
      />
      <DesktopView
        steps={steps}
        currentStep={currentStep}
        progressPercentage={progressPercentage}
      />
      <MobileView steps={steps} currentStep={currentStep} />
    </div>
  )
}

export default OrdersProgressBar
